import React from "react";
import { TLShapeId, TLShape, useEditor, useValue } from "@tldraw/tldraw";
import cn from "classnames";
import CustomColorPicker from "./CustomColorPicker";
import TextParameters from "./TextParameters";
import SideBarSlider from "./SidebarSlider";
import AlignActions from "./AlignActions";
import RotateComponent from "./RotateComponent";
import useTooltipStyles from "./style";
import FlipComponent from "./FlipComponent";
import MaskComponent from "./MaskComponent";
import SidebarBottomTool, { ToggleEyesIcon } from "./SidebarBottomTool";
import { LineArrowColorSidebar } from "./LineArrowColorSidebar";
import { LabelUtil, LabelUtilType } from "../state/format-shapes/LabelUtil";
import QrColorPicker from "./QrColorPicker";
import { useStore } from "../../../../../store";
import { StoreType } from "../../../../../store/types";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import { MinusIcon } from "@radix-ui/react-icons";
import { Image } from "../../components/SideBar/Aside/components/Image/Image";
import { Icons } from "../../components/SideBar/Aside/components/Icons/Icons";
import Tooltip from "@material-ui/core/Tooltip";
import { QrCode } from "../../components/SideBar/Aside/components";
import BrandKitAssetsPanel from "../../components/SideBar/Aside/components/BrandKit/BrandKitAssetsPanel";
import ImageToolIcon from "components/icons/components/ImageToolIcon";
import IconsToolIcon from "components/icons/components/IconsToolIcon";
import QRcodeIcon from "components/icons/components/QRcodeIcon";
import TextToolIcon from "components/icons/components/TextToolIcon";
import ShapeToolIcon from "components/icons/components/ShapeToolIcon";
import CloseIcon from "components/icons/components/CloseIcon";
import CopyIcon from "components/icons/components/CopyIcon";
import { getCosmeticQrShortLink } from "utils/qrShortLink";
import { resetQrShapesToDefault } from "utils/qrCode";
import TrashIcon from "components/icons/components/TrashIcon";
import { makeStyles } from "@material-ui/core";
import { isTypingInInput } from "../utils";
import { SECTIONS } from "../../constants";
import { useCommentsStore } from "../../../../../store/comments";
import ThreadsListPanel from "./Comments/ThreadsListPanel";

// Check if the required address box fields are all filled
// Format: "Address1, Address2, City, ST 12345" (single line, comma-separated)
const isAddressBoxFilled = (returnAddress: string): boolean => {
  const t = (returnAddress || "").trim();
  if (!t || t.includes("{{return_address}}")) return false;
  // Normalize legacy newline format to single line
  const singleLine = t.includes("\n")
    ? t
        .split(/\n/)
        .map((s) => s.trim())
        .filter(Boolean)
        .join(", ")
    : t;
  // Must end with ", ST 12345" pattern
  const endMatch = singleLine.match(/,\s*([A-Za-z]{2})\s+(\d{5}(-\d{4})?)\s*$/);
  if (!endMatch) return false;
  const before = singleLine.slice(0, endMatch.index!).trim();
  const parts = before
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  // Need at least address1 and city (2 parts)
  return parts.length >= 2;
};

const useStyles = makeStyles({
  tooltipCopied: {
    padding: "6px 10px",
    backgroundColor: "#eee",
    color: "#000",
    width: "auto",
    maxWidth: "none",
  },
  tooltipArrow: {
    "&::before": {
      color: "#f9f9f9",
    },
  },
});

// Canvas Front/Back labels are never user-selectable (single source of truth).
const NON_SELECTABLE_LABEL_IDS = ["shape:frontLabel", "shape:backLabel"];

function CustomStylePanel(props: any) {
  const classes = useStyles();
  const tooltipClasses = useTooltipStyles();
  const [selectedShape, setSelectedShape] = React.useState<any>(null);
  const [selectedFont, setSelectedFont] = React.useState("Proxima Nova");
  const [showColorPickerSetMode, setShowColorPickerSetMode] = React.useState(false);
  const [isSelectedPipette, setIsSelectedPipette] = React.useState(false);

  const editor = useEditor();

  // Store access for QR color settings and image/icons panel state
  const {
    qrCodeSettings: { qrPixelColor, qrBackgroundColor },
    updateQrCodeSettings,
    campaign,
    updateCampaign,
    showImagePanel,
    setShowImagePanel,
    showIconsPanel,
    setShowIconsPanel,
    showQRcodePanel,
    setShowQRcodePanel,
    brandKitAssetsPanel,
    setBrandKitAssetsPanel,
  }: StoreType = useStore();

  const isCommentsHistoryOpen = useCommentsStore((s) => s.isHistoryOpen);
  const closeCommentsHistory = useCommentsStore((s) => s.closeHistory);

  // Right sidebar collapse state
  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = React.useState(false);

  // Local state for QR colors to handle per-QR customization
  const [localQrPixelColor, setLocalQrPixelColor] = React.useState((qrPixelColor || "#000000").toLowerCase());
  const [localQrBackgroundColor, setLocalQrBackgroundColor] = React.useState("#ffffff"); // Always start with white background
  const [showBackgroundColorPicker, setShowBackgroundColorPicker] = React.useState(true);
  const [isBackgroundColorVisible, setIsBackgroundColorVisible] = React.useState(true);

  // Ref to track pending QR updates for throttling
  const qrUpdateTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  // Ref to prevent useEffect from overwriting when actively adding background color
  const isAddingBackgroundColorRef = React.useRef(false);
  // Refs to track the previous value of selectedShape and if a panel is opening
  const prevSelectedShapeRef = React.useRef<any>(null);
  const isOpeningPanelRef = React.useRef<boolean>(false);
  // State for editable QR code URL
  const [editableQrCodeUrl, setEditableQrCodeUrl] = React.useState("");
  // State to control edit mode for QR code URL
  const [isEditingQrCodeUrl, setIsEditingQrCodeUrl] = React.useState(false);
  // State to control hover for QR code URL label
  const [isHoveringQrCodeUrl, setIsHoveringQrCodeUrl] = React.useState(false);
  // State to control hover for QR code URL input
  const [isHoveringQrCodeUrlInput, setIsHoveringQrCodeUrlInput] = React.useState(false);
  // State to control focus for QR code URL input
  const [isFocusedQrCodeUrlInput, setIsFocusedQrCodeUrlInput] = React.useState(false);
  // Ref for the input element
  const qrCodeUrlInputRef = React.useRef<HTMLInputElement | null>(null);
  const qrLinkBoxRef = React.useRef<HTMLDivElement | null>(null);

  // Listen for shake event from BottomToolPanel when QR is auto-created
  React.useEffect(() => {
    const handleShake = () => {
      const el = qrLinkBoxRef.current;
      if (!el) return;
      el.classList.add("qr-link-shake");
      setTimeout(() => el.classList.remove("qr-link-shake"), 600);
    };
    window.addEventListener("qr-link-shake", handleShake);
    return () => window.removeEventListener("qr-link-shake", handleShake);
  }, []);

  // Trigger shake animation when a QR code shape is repositioned on the canvas,
  // to remind the user to enter a URL. Debounced so the shake only fires once
  // after the move gesture ends (not on every x/y change during the drag).
  React.useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const handleShapeChange = (shapeBefore: any, shapeAfter: any) => {
      const isQR =
        shapeAfter?.type === "customImage" &&
        ((shapeAfter.props as any)?.qrCodeLink !== undefined ||
          (shapeAfter.props as any)?.assetId?.toString().includes("qr") ||
          shapeAfter.id?.toString().includes("qrCode"));
      if (!isQR) return;
      if (shapeBefore.x === shapeAfter.x && shapeBefore.y === shapeAfter.y) return;
      // If the QR already has a valid URL, don't nag the user with a shake.
      const qrLink = ((shapeAfter.props as any)?.qrCodeLink || "").trim();
      if (qrLink && qrLink !== "https://" && qrLink !== "http://") return;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("qr-link-shake"));
        timeoutId = null;
      }, 150);
    };
    const unregister = editor.sideEffects.registerAfterChangeHandler("shape", handleShapeChange);
    return () => {
      unregister();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [editor]);
  const [copiedTooltipOpen, setCopiedTooltipOpen] = React.useState(false);

  // Override deleteShapes method to prevent deletion of LabelUtil shapes
  React.useEffect(() => {
    const originalDeleteShapes = editor.deleteShapes;
    // @ts-ignore
    editor.deleteShapes = function (shapes: TLShapeId[]) {
      const shapesToDelete: TLShapeId[] = [];
      for (const shapeId of shapes) {
        const shape = editor.getShape(shapeId);
        if (shape && shape.type === "customLabel") {
          const util = editor.getShapeUtil(shape.type) as LabelUtil;
          try {
            const canDelete = util.onBeforeDelete(shape as LabelUtilType);
            if (!canDelete) {
              continue; // Skip this shape
            }
          } catch (error: any) {
            continue; // Skip this shape
          }
        }
        shapesToDelete.push(shapeId);
      }
      if (shapesToDelete.length > 0) {
        return originalDeleteShapes.call(editor, shapesToDelete as any);
      }
    };

    return () => {
      editor.deleteShapes = originalDeleteShapes; // Restore original method on unmount
    };
  }, [editor]);

  React.useEffect(() => {
    const originalGroupShapes = editor.groupShapes;

    // Override groupShapes to exclude customLabel shapes
    editor.groupShapes = function (shapeIds) {
      const filteredShapeIds = shapeIds.filter((shapeId) => {
        const shape = editor.getShape(shapeId);
        return shape?.type !== "customLabel"; // Exclude customLabel shapes
      });

      if (filteredShapeIds.length > 1) {
        return originalGroupShapes.call(editor, filteredShapeIds as TLShapeId[]);
      }
      return editor; // Ensure an Editor instance is always returned
    };

    return () => {
      editor.groupShapes = originalGroupShapes; // Restore original method on unmount
    };
  }, [editor]);

  const selectedShapeIds = useValue("isSelected", () => editor.getSelectedShapeIds(), [editor]);
  // Resolve the selected shape *records* reactively so that whenever any
  // selected shape mutates (color, size, position…), `selectedShapes` reflects
  // the fresh data on the next render. Consumers like ColorPickerPopover
  // build new shape objects from this array (`{ ...shape, props: ... }`) and
  // then write them back with `editor.updateShape(...)` — if the array goes
  // stale, those writes overwrite freshly-applied changes with the previous
  // values (e.g. a color picker click reverts the color a few seconds later
  // when something triggers a re-iteration).
  //
  // The perf regression that previously motivated memoizing by ID set is
  // already covered: the bulk-selection bail-out in NewEffectSidebar /
  // OutlineSidebar / StyleMenu hides the per-shape `.map()` rendering at
  // >30 selected, so the heavy work doesn't happen even though this array
  // refreshes.
  const selectedShapes = useValue(
    "selectedShapes",
    () => {
      const ids = editor.getSelectedShapeIds();
      return ids.map((id) => editor.getShape(id)).filter((shape): shape is NonNullable<typeof shape> => !!shape);
    },
    [editor]
  );

  // Auto-expand right sidebar when selection changes to a different shape
  React.useEffect(() => {
    if (selectedShapeIds.length > 0) {
      setIsRightSidebarCollapsed(false);
    }
  }, [selectedShapeIds]);

  const hasSelection = selectedShapeIds.length > 0;

  // Close Comments history sidebar when a shape is selected
  React.useEffect(() => {
    if (hasSelection && isCommentsHistoryOpen) {
      closeCommentsHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSelection]);

  // Toggle right sidebar via custom event (from context menu)
  React.useEffect(() => {
    const handleToggleSidebar = () => {
      setIsRightSidebarCollapsed((prev) => !prev);
    };
    window.addEventListener("toggleSidebar", handleToggleSidebar);
    return () => window.removeEventListener("toggleSidebar", handleToggleSidebar);
  }, []);

  // Helper function to check if a shape is a QR code
  const isQRCodeShape = (shape: any) => {
    if (!shape) return false;
    return (
      shape.type === "customImage" &&
      ((shape.props as any)?.qrCodeLink ||
        (shape.props as any)?.assetId?.toString().includes("qr") ||
        shape.id?.toString().includes("qrCode"))
    );
  };

  // Helper functions for QR code multiselect logic
  const getFirstSelectedQRCodeShape = () => {
    return selectedShapes.find((shape) => isQRCodeShape(shape));
  };

  const getAllSelectedQRCodes = () => {
    return selectedShapes.filter((shape) => isQRCodeShape(shape));
  };

  const isAnyQRCodeSelected = () => {
    return selectedShapes.some((shape) => isQRCodeShape(shape));
  };

  const selectionHasOnlyQRCodes = () => {
    return selectedShapes.length > 0 && selectedShapes.every((shape) => isQRCodeShape(shape));
  };

  // Synchronize selected shape on selection change
  React.useEffect(() => {
    // Front/Back labels are not user-selectable. Single-select is handled below
    // (deselect + open settings); for multi-select a marquee or select-all would
    // otherwise grab them alongside real shapes — strip them out here.
    if (selectedShapeIds.length > 1 && selectedShapeIds.some((id) => NON_SELECTABLE_LABEL_IDS.includes(id))) {
      editor.setSelectedShapes(selectedShapeIds.filter((id) => !NON_SELECTABLE_LABEL_IDS.includes(id)));
      return; // effect re-runs with the pruned selection
    }

    if (selectedShapeIds.length === 1) {
      const shape = editor.getShape(selectedShapeIds[0]);
      if (shape && NON_SELECTABLE_LABEL_IDS.includes(shape.id)) {
        editor.selectNone();
        window.dispatchEvent(new CustomEvent("openSettingsPanel"));
        return;
      }
      if (shape?.type === "customAddressBox") {
        // Show right sidebar with alignment tools only
        setSelectedShape(shape);
        return;
      }
      if (shape) {
        setSelectedShape(shape);
        // Update editable URL when QR code is selected
        if (isQRCodeShape(shape)) {
          const qrCodeLink = (shape.props as any)?.qrCodeLink || "";
          setEditableQrCodeUrl(qrCodeLink);
          setIsEditingQrCodeUrl(false); // Reset edit mode on selection change
        }
      } else {
        setSelectedShape(null);
        setEditableQrCodeUrl("");
        setIsEditingQrCodeUrl(false);
      }
    } else if (selectedShapeIds.length > 1) {
      // For multiple selection, use the first shape as representative
      const firstShape = editor.getShape(selectedShapeIds[0]);
      if (firstShape) {
        setSelectedShape(firstShape);
        // Update editable URL when QR code is selected
        if (isQRCodeShape(firstShape)) {
          const qrCodeLink = (firstShape.props as any)?.qrCodeLink || "";
          setEditableQrCodeUrl(qrCodeLink);
          setIsEditingQrCodeUrl(false); // Reset edit mode on selection change
        }
      } else {
        setSelectedShape(null);
        setEditableQrCodeUrl("");
        setIsEditingQrCodeUrl(false);
      }
    } else {
      setSelectedShape(null);
      setEditableQrCodeUrl("");
      setIsEditingQrCodeUrl(false);
    }
  }, [selectedShapeIds, editor]);

  // Register sideEffects to track shape changes
  React.useEffect(() => {
    // Function called after shape change
    // Bail-out threshold for the multi-select "force re-render" branch.
    // Above this, the right sidebar already shows the bulk-selection stub
    // (no per-shape controls — see BULK_SELECTION_RENDER_THRESHOLD in
    // NewEffectSidebar/OutlineSidebar), so the panel has nothing to refresh
    // and the work below is pure cost. Keep in sync with that threshold.
    const STYLE_PANEL_MULTI_SYNC_THRESHOLD = 30;

    // Use a Set for O(1) membership checks — when the user is dragging 2000
    // shapes the previous `.includes()` array search ran O(N) for each of
    // the 2000 mutation events, i.e. O(N²) per pointer move.
    const selectedShapeIdSet = new Set(selectedShapeIds);

    const handleShapeChange = (changedShape: any) => {
      if (selectedShape && changedShape.id === selectedShape.id) {
        setSelectedShape(changedShape);
      } else if (
        selectedShapeIds.length > 1 &&
        selectedShapeIds.length <= STYLE_PANEL_MULTI_SYNC_THRESHOLD &&
        selectedShapeIdSet.has(changedShape.id)
      ) {
        // Force a re-render to ensure the UI reflects changes in any selected shape.
        // The old approach called `editor.setSelectedShapes(currentSelectedIds)`
        // which re-triggered the entire selection-change reactive cascade —
        // catastrophic during multi-shape drags. Above the threshold we skip
        // this entirely because the sidebar is in bulk-stub mode anyway.
        const currentSelectedIds = editor.getSelectedShapeIds();
        if (currentSelectedIds.length > 0) {
          editor.setSelectedShapes(currentSelectedIds);
        }
      }
    };

    const unregisterAfterChange = editor.sideEffects.registerAfterChangeHandler("shape", handleShapeChange);

    return () => {
      unregisterAfterChange(); // Clean up sideEffect on unmount
    };
  }, [selectedShape, selectedShapeIds, editor]);

  // Sync local QR colors when selection changes
  React.useEffect(() => {
    // Don't sync if we're actively adding background color
    if (isAddingBackgroundColorRef.current) {
      return;
    }

    if (isQRCodeSelected()) {
      const selectedQRCodes = getAllSelectedQRCodes();

      if (selectedQRCodes.length > 0) {
        // Use colors from first QR code as reference for the UI (single select mode)
        if (selectedQRCodes.length === 1) {
          const firstQR = selectedQRCodes[0];
          if (firstQR) {
            const shapePixelColor = ((firstQR.props as any)?.qrPixelColor || qrPixelColor || "#000000").toLowerCase();
            let shapeBgColor = (firstQR.props as any)?.qrBackgroundColor || "";

            setLocalQrPixelColor(shapePixelColor);

            // If QR has no background color, set it to white by default
            if (!shapeBgColor || shapeBgColor === "" || shapeBgColor === "#0000000") {
              shapeBgColor = "#ffffff";
              // Update the shape and asset with white background
              editor.updateShape({
                id: firstQR.id,
                type: firstQR.type,
                props: {
                  ...firstQR.props,
                  qrBackgroundColor: "#ffffff",
                  isBackgroundVisible: true,
                },
              } as any);
              updateQrCodeAsset(firstQR, shapePixelColor, "#ffffff");
            }

            // Always show picker with background color (normalize to lowercase)
            setLocalQrBackgroundColor(shapeBgColor.toLowerCase());
            setShowBackgroundColorPicker(true);
            // Read the visibility state from the QR shape, default to true if not set
            const shapeIsBackgroundVisible = (firstQR as any)?.props?.isBackgroundVisible;
            setIsBackgroundColorVisible(shapeIsBackgroundVisible !== undefined ? shapeIsBackgroundVisible : true);
          }
        }
      }
    } else {
      // No QR selected - reset to defaults (normalize to lowercase)
      setLocalQrPixelColor((qrPixelColor || "#000000").toLowerCase());
      setLocalQrBackgroundColor("#ffffff");
      setShowBackgroundColorPicker(true);
      setIsBackgroundColorVisible(true);
    }
  }, [qrPixelColor, selectedShapes]);

  const handleSliderChange = (property: string, value: number) => {
    selectedShapes.forEach((shape) => {
      if (!shape || !shape.props.hasOwnProperty(property)) return;

      const updatedProps = { ...(shape.props as Record<string, unknown>), [property]: value };
      editor.updateShape({
        ...shape,
        isLocked: false,
        props: updatedProps as typeof shape.props,
      });
    });
  };

  // Handle QR pixel color change - updates all selected QR codes independently
  const handleQrPixelColorChange = async (color: string) => {
    const normalizedColor = color.toLowerCase();
    setLocalQrPixelColor(normalizedColor);
    updateQrCodeSettings({ qrPixelColor: normalizedColor });

    // Update all selected QR code shapes independently
    const selectedQRCodes = getAllSelectedQRCodes();
    if (selectedQRCodes.length > 0) {
      // Update each QR code with its own background color
      await Promise.all(
        selectedQRCodes.map(async (qrShape) => {
          if (!qrShape) return;
          // Get current background color from each shape's props
          const currentBgColor = (qrShape.props as any)?.qrBackgroundColor || "";

          // Store the pixel color in the shape properties for persistence
          editor.updateShape({
            id: qrShape.id,
            type: qrShape.type,
            props: {
              ...qrShape.props,
              qrPixelColor: normalizedColor,
            },
          } as any);

          // Update the asset for this specific QR code
          await updateQrCodeAsset(qrShape, normalizedColor, currentBgColor);
        })
      );
    }
  };

  // Handle QR pixel color change with transparent background by default - updates all selected QR codes
  const handleQrPixelColorChangeWithTransparency = async (color: string) => {
    const normalizedColor = color.toLowerCase();
    setLocalQrPixelColor(normalizedColor);
    updateQrCodeSettings({ qrPixelColor: normalizedColor });

    // Update all selected QR code shapes independently
    const selectedQRCodes = getAllSelectedQRCodes();
    if (selectedQRCodes.length > 0) {
      selectedQRCodes.forEach((qrShape) => {
        if (!qrShape) return;
        // Get the current background color from each shape's props
        const currentBgColor = (qrShape.props as any)?.qrBackgroundColor || "";

        // Store the pixel color in the shape properties for persistence
        editor.updateShape({
          id: qrShape.id,
          type: qrShape.type,
          props: {
            ...qrShape.props,
            qrPixelColor: normalizedColor,
          },
        } as any);

        // Use throttled version for smooth real-time updates while dragging
        updateQrCodeAssetThrottled(qrShape, normalizedColor, currentBgColor);
      });
    }
  };

  // Handle removing background color - updates all selected QR codes
  const handleRemoveBackgroundColor = async () => {
    // Prevent useEffect from overwriting during this operation
    isAddingBackgroundColorRef.current = true;

    setLocalQrBackgroundColor("");
    setShowBackgroundColorPicker(false);
    setIsBackgroundColorVisible(true);
    updateQrCodeSettings({ qrBackgroundColor: "" });

    // Update all selected QR code assets to remove background color
    const selectedQRCodes = getAllSelectedQRCodes();
    if (selectedQRCodes.length > 0) {
      await Promise.all(
        selectedQRCodes.map(async (qrShape) => {
          if (!qrShape) return;
          // Get current pixel color from each shape's props
          const currentPixelColor = (qrShape.props as any)?.qrPixelColor || localQrPixelColor || "#000000";

          // Remove the background color from shape properties
          editor.updateShape({
            id: qrShape.id,
            type: qrShape.type,
            props: {
              ...qrShape.props,
              qrBackgroundColor: "",
              isBackgroundVisible: true,
            },
          } as any);
          await updateQrCodeAsset(qrShape, currentPixelColor, "");
        })
      );
    }

    // Clear the flag after operations complete
    setTimeout(() => {
      isAddingBackgroundColorRef.current = false;
    }, 300);
  };

  // Handle background color visibility toggle - updates all selected QR codes
  const handleBackgroundColorVisibilityToggle = async () => {
    const newVisibilityState = !isBackgroundColorVisible;
    setIsBackgroundColorVisible(newVisibilityState);

    // Update all selected QR code assets with visible or hidden background
    const selectedQRCodes = getAllSelectedQRCodes();
    if (selectedQRCodes.length > 0) {
      await Promise.all(
        selectedQRCodes.map(async (qrShape) => {
          if (!qrShape) return;
          // Get current colors from each shape's props
          const currentPixelColor = (qrShape.props as any)?.qrPixelColor || localQrPixelColor || "#000000";
          const currentBgColor = (qrShape.props as any)?.qrBackgroundColor || "";

          const backgroundColor = newVisibilityState ? currentBgColor : "";
          await updateQrCodeAsset(qrShape, currentPixelColor, backgroundColor);

          // Persist the visibility state in the shape
          editor.updateShape({
            id: qrShape.id,
            type: qrShape.type,
            props: {
              ...qrShape.props,
              isBackgroundVisible: newVisibilityState,
              qrBackgroundColor: newVisibilityState ? currentBgColor : currentBgColor,
            },
          } as any);
        })
      );
    }
  };

  // Helper function to update QR code asset
  const updateQrCodeAsset = async (shape: any, pixelColor: string, backgroundColor: string, qrCodeLink?: string) => {
    const QRCode = (await import("qrcode")).default;
    const rawLink = qrCodeLink || shape.props?.qrCodeLink || "oppizi.com";
    // Render the QR image on the cosmetic short link; the shape prop keeps the raw URL.
    const link = rawLink === "oppizi.com" ? rawLink : await getCosmeticQrShortLink(rawLink);

    try {
      // Generate new QR code with updated colors
      const svgQrCode = await QRCode.toString(link, {
        type: "svg",
        margin: 0,
        width: 300,
        color: {
          dark: pixelColor,
          light: backgroundColor || "#0000",
        },
      });

      const newSrc = `data:image/svg+xml;base64,${btoa(svgQrCode)}`;

      // Update the asset
      editor.updateAssets([
        {
          id: shape.props.assetId,
          type: "image",
          typeName: "asset",
          meta: {},
          props: {
            name: "image",
            src: newSrc,
            w: shape.props.w || 150,
            h: shape.props.h || 150,
            mimeType: "image/svg+xml",
            isAnimated: false,
          },
        },
      ]);
    } catch (error) {
      console.warn("Error updating QR code asset:", error);
    }
  };

  // Clear the QR link everywhere (QR shape props + campaign) so an emptied field stays empty
  // and the print pipeline doesn't fall back to the old campaign-level link.
  const clearQrCodeLink = React.useCallback(() => {
    // Clear the link off the QR shape(s) and re-render the image to the default QR.
    void resetQrShapesToDefault(editor);
    updateCampaign({ qrCodeLink: null });
    if (campaign?.id) {
      updateCampaignExtraData({ campaignId: campaign.id, qrCodeLink: null }).catch(() => {});
    }
  }, [editor, updateCampaign, campaign]);

  // Function to update QR code URL
  const updateQrCodeUrl = React.useCallback(
    async (newUrl: string) => {
      const selectedQRCodes = getAllSelectedQRCodes();
      if (selectedQRCodes.length === 0) return;

      // Normalize QR code link (add protocol if missing)
      const normalizedQrLink = newUrl?.trim().length
        ? newUrl.trim().startsWith("https://") || newUrl.trim().startsWith("http://")
          ? newUrl.trim()
          : `https://${newUrl.trim()}`
        : "";

      if (!normalizedQrLink) return;

      // Update all selected QR codes
      for (const qrShape of selectedQRCodes) {
        if (!qrShape) continue;

        // Get current colors from shape props
        const currentPixelColor = (qrShape.props as any)?.qrPixelColor || localQrPixelColor || "#000000";
        const currentBackgroundColor = (qrShape.props as any)?.qrBackgroundColor || localQrBackgroundColor || "";

        // Generate new QR code with the new URL and current colors. The QR image is
        // built on the cosmetic short link; the shape prop keeps the raw URL below.
        const QRCode = (await import("qrcode")).default;
        const qrImageLink = await getCosmeticQrShortLink(normalizedQrLink);
        const qrCodeWithTransparentBg = await QRCode.toString(qrImageLink, {
          type: "svg",
          margin: 0,
          width: 300,
          color: {
            dark: currentPixelColor,
            light: currentBackgroundColor || "#0000",
          },
        });

        const existingAssetId = (qrShape.props as any)?.assetId;

        // Update the asset with new QR code
        if (existingAssetId) {
          editor.updateAssets([
            {
              id: existingAssetId as any,
              type: "image",
              typeName: "asset",
              props: {
                name: "image",
                src: `data:image/svg+xml;base64,${btoa(qrCodeWithTransparentBg)}`,
                w: 150,
                h: 150,
                mimeType: "image/svg+xml",
                isAnimated: false,
              },
            },
          ]);
        }

        // Update the shape with new QR link
        editor.updateShape({
          ...qrShape,
          props: {
            ...qrShape.props,
            qrCodeLink: normalizedQrLink,
          },
        });
      }
    },
    [getAllSelectedQRCodes, localQrPixelColor, localQrBackgroundColor, editor]
  );

  // Throttled version of updateQrCodeAsset for real-time updates while dragging
  const updateQrCodeAssetThrottled = React.useCallback(
    (shape: any, pixelColor: string, backgroundColor: string) => {
      // Clear any pending update
      if (qrUpdateTimeoutRef.current) {
        clearTimeout(qrUpdateTimeoutRef.current);
      }

      // Schedule update after a short delay (throttle)
      qrUpdateTimeoutRef.current = setTimeout(() => {
        updateQrCodeAsset(shape, pixelColor, backgroundColor);
      }, 50); // Update every 50ms max while dragging — snappier color response
    },
    [editor]
  );

  // Handle delete QR code
  const handleDeleteQRCode = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent triggering edit mode
      const selectedQRCodes = getAllSelectedQRCodes();
      if (selectedQRCodes.length > 0) {
        const qrIds = selectedQRCodes.filter((qr) => qr != null).map((qr) => qr!.id);
        editor.deleteShapes(qrIds);
      }
    },
    [getAllSelectedQRCodes, editor]
  );

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in search, return address, etc.
      if (isTypingInInput(event)) return;

      if (event.key === "b") {
        setShowColorPickerSetMode(!showColorPickerSetMode);
      }
      if (event.shiftKey && event.key === "I") {
        setIsSelectedPipette(!isSelectedPipette);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showColorPickerSetMode, isSelectedPipette]);

  // Auto-close the pick-color (pipette) panel when:
  //   1. The user clicks outside the right sidebar — covers clicks on the
  //      canvas, top toolbar, left sidebar, header, anything else. We anchor
  //      the "inside" check on `[data-tour="right-sidebar"]` which is the
  //      single wrapper that holds the picker panel.
  //   2. The user activates any other tool from the bottom panel — once
  //      the current tool changes away from "select" the pipette no longer
  //      has a target to pick a color for, so dismiss it to avoid the
  //      panel sticking around while the user is drawing elsewhere.
  // Tool change is observed via `useValue` (tldraw's reactive subscription)
  // so we don't poll. The outside-click listener is attached deferred so the
  // same Shift+I keydown that opened the picker doesn't immediately close it.
  const currentToolIdForPipette = useValue("currentToolIdForPipette", () => editor.getCurrentToolId(), [editor]);
  React.useEffect(() => {
    if (!isSelectedPipette) return;
    if (currentToolIdForPipette !== "select") {
      setIsSelectedPipette(false);
      return;
    }
    const handleOutsideMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-tour="right-sidebar"]')) return;
      setIsSelectedPipette(false);
    };
    const tid = window.setTimeout(() => {
      document.addEventListener("mousedown", handleOutsideMouseDown);
    }, 0);
    return () => {
      window.clearTimeout(tid);
      document.removeEventListener("mousedown", handleOutsideMouseDown);
    };
  }, [isSelectedPipette, currentToolIdForPipette]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (qrUpdateTimeoutRef.current) {
        clearTimeout(qrUpdateTimeoutRef.current);
      }
      // Clear the flag in case the component unmounts during operation
      isAddingBackgroundColorRef.current = false;
    };
  }, []);

  // Reset isOpeningPanelRef when the panels are opened
  React.useEffect(() => {
    if (showImagePanel || showIconsPanel || showQRcodePanel || brandKitAssetsPanel) {
      // Set the flag immediately when the panel is opened
      isOpeningPanelRef.current = true;
      // Reset after a delay to allow the panel to open and avoid closing
      // The delay must be sufficient for any change in selectedShape to be processed
      const timeoutId = setTimeout(() => {
        isOpeningPanelRef.current = false;
      }, 200); // Increased to 200ms to give more time

      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      // If the panel is closed, reset the flag immediately
      isOpeningPanelRef.current = false;
    }
  }, [showImagePanel, showIconsPanel, showQRcodePanel, brandKitAssetsPanel]);

  // Close Image panel when a shape is selected (but not when panel is being opened)
  React.useEffect(() => {
    // Only close if selectedShape exists and the panel is open
    // Don't close if the panel is being opened intentionally (isOpeningPanelRef)
    if (selectedShape && showImagePanel && !isOpeningPanelRef.current) {
      setShowImagePanel(false);
    }
    // Update the ref to track the previous value
    prevSelectedShapeRef.current = selectedShape;
  }, [selectedShape, showImagePanel, setShowImagePanel]);

  // Close Icons panel when a shape is selected (but not when panel is being opened)
  React.useEffect(() => {
    // Only close if selectedShape exists and the panel is open
    // Don't close if the panel is being opened intentionally (isOpeningPanelRef)
    if (selectedShape && showIconsPanel && !isOpeningPanelRef.current) {
      setShowIconsPanel(false);
    }
    // Update the ref to track the previous value
    prevSelectedShapeRef.current = selectedShape;
  }, [selectedShape, showIconsPanel, setShowIconsPanel]);

  // Close QRCode panel when a shape is selected (but not when panel is being opened)
  React.useEffect(() => {
    // Only close if selectedShape exists and the panel is open
    // Don't close if the panel is being opened intentionally (isOpeningPanelRef)
    if (selectedShape && showQRcodePanel && !isOpeningPanelRef.current) {
      setShowQRcodePanel(false);
    }
    // Update the ref to track the previous value
    prevSelectedShapeRef.current = selectedShape;
  }, [selectedShape, showQRcodePanel, setShowQRcodePanel]);

  // Close Brand Kit assets panel when a shape is selected (but not when panel is being opened)
  React.useEffect(() => {
    if (selectedShape && brandKitAssetsPanel && !isOpeningPanelRef.current) {
      setBrandKitAssetsPanel(null);
    }
    prevSelectedShapeRef.current = selectedShape;
  }, [selectedShape, brandKitAssetsPanel, setBrandKitAssetsPanel]);

  // Close Image panel when clicking outside the right sidebar and activate select tool
  React.useEffect(() => {
    if (!showImagePanel) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      // MUI Popover/Menu/Dialog render in a portal at document.body, so a
      // click inside them is technically "outside" the sidebar. Bail out
      // here so opening a popover inside the panel (e.g. the orientation
      // filter on Image) doesn't close the whole panel on selection.
      if (target.closest(".MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiDialog-root, .MuiAutocomplete-popper"))
        return;
      const sidebar = document.querySelector('[class*="right-0"]') as HTMLElement;
      const imagePanel = sidebar?.querySelector('[class*="right-0"]') as HTMLElement;

      // Check if click is outside the right sidebar
      if (imagePanel && !imagePanel.contains(event.target as Node)) {
        setShowImagePanel(false);

        // Activate select tool (cursor/move) if not already selected
        if (editor.getCurrentToolId() !== "select") {
          editor.setCurrentTool("select");
        }
      }
    };

    // Add event listener with a small delay to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showImagePanel, editor, setShowImagePanel]);

  // Close Icons panel when clicking outside the right sidebar and activate select tool
  React.useEffect(() => {
    if (!showIconsPanel) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      // See Image panel handler above — ignore clicks inside MUI portals
      // so popovers/menus opened from within the panel don't trigger a
      // false outside-click.
      if (target.closest(".MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiDialog-root, .MuiAutocomplete-popper"))
        return;
      const sidebar = document.querySelector('[class*="right-0"]') as HTMLElement;
      const iconsPanel = sidebar?.querySelector('[class*="right-0"]') as HTMLElement;

      // Check if click is outside the right sidebar
      if (iconsPanel && !iconsPanel.contains(event.target as Node)) {
        setShowIconsPanel(false);

        // Activate select tool (cursor/move) if not already selected
        if (editor.getCurrentToolId() !== "select") {
          editor.setCurrentTool("select");
        }
      }
    };

    // Add event listener with a small delay to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showIconsPanel, editor, setShowIconsPanel]);

  // Function to check if a QR code is selected (works for single and multi-select)
  const isQRCodeSelected = () => {
    // For single selection, use selectedShape
    if (selectedShapes.length === 1 && selectedShape) {
      return (
        selectedShape.type === "customImage" &&
        (selectedShape.props?.assetId?.toString().includes("qr") || selectedShape.id?.includes("qrCode"))
      );
    }

    // For multi-select, check if any QR code is selected
    return isAnyQRCodeSelected();
  };

  // Simple function to get QR background color from shape (works for single and multi-select)
  const getQRBackgroundColorFromShape = () => {
    if (!isQRCodeSelected()) {
      return null;
    }

    // For single selection, use selectedShape
    if (selectedShapes.length === 1 && selectedShape) {
      return selectedShape.props?.qrBackgroundColor;
    }

    // For multi-select, use the first QR code shape
    const qrShape = getFirstSelectedQRCodeShape();
    return (qrShape as any)?.props?.qrBackgroundColor;
  };

  // Helper function to get the header title based on selected shape or panel
  const getSidebarHeaderTitle = () => {
    // Check for panels first
    if (brandKitAssetsPanel === "image") return "Brand Images";
    if (brandKitAssetsPanel === "logo") return "Brand Logos";
    if (brandKitAssetsPanel === "icon") return "Brand Icons";
    if (showImagePanel) return "Image";
    if (showIconsPanel) return "Icon";
    if (showQRcodePanel) return "QR Code";

    // Check for selected shapes
    if (!selectedShape) return null;

    // Check for Address Box
    if (selectedShape.type === "customAddressBox") return "Address Box";

    // Check for QR Code
    if (isQRCodeSelected()) return "QR Code";

    // Check for Icon (customSvg)
    if (selectedShape.type === "customSvg") return "Icon";

    // Check for Image (customImage but not QR)
    if (selectedShape.type === "customImage" && !isQRCodeSelected()) return "Image";

    // Check for Text
    if (selectedShape.type === "customText") return "Text";

    // Check for basic shapes (rect, ellipse, triangle, line, arrow, etc.)
    const shapeTypes = [
      "customRect",
      "customEllipse",
      "customTriangle",
      "line",
      "arrow",
      "group",
      "customDraw",
      "customHighlight",
    ];
    if (shapeTypes.includes(selectedShape.type)) return "Shape";

    // Default fallback
    return "Shape";
  };

  const getSidebarHeaderIcon = () => {
    // Check for panels first
    if (brandKitAssetsPanel === "image") return <ImageToolIcon size="18px" />;
    if (brandKitAssetsPanel === "logo") return <ImageToolIcon size="18px" />;
    if (brandKitAssetsPanel === "icon") return <IconsToolIcon size="18px" />;
    if (showImagePanel) return <ImageToolIcon size="18px" />;
    if (showIconsPanel) return <IconsToolIcon size="18px" />;
    if (showQRcodePanel) return <QRcodeIcon size="18px" />;

    // Check for selected shapes
    if (!selectedShape) return null;

    // Check for Address Box
    if (selectedShape.type === "customAddressBox") return <ShapeToolIcon size="18px" />;

    // Check for QR Code
    if (isQRCodeSelected()) return <QRcodeIcon size="18px" />;

    // Check for Icon (customSvg)
    if (selectedShape.type === "customSvg") return <IconsToolIcon size="18px" />;

    // Check for Image (customImage but not QR)
    if (selectedShape.type === "customImage" && !isQRCodeSelected()) return <ImageToolIcon size="18px" />;

    // Check for Text
    if (selectedShape.type === "customText") return <TextToolIcon size="18px" />;

    // Check for basic shapes (rect, ellipse, triangle, line, arrow, etc.)
    const shapeTypes = [
      "customRect",
      "customEllipse",
      "customTriangle",
      "line",
      "arrow",
      "group",
      "customDraw",
      "customHighlight",
    ];
    if (shapeTypes.includes(selectedShape.type)) return <ShapeToolIcon size="18px" />;

    // Default fallback
    return <ShapeToolIcon size="18px" />;
  };

  const CommentsHeaderIcon = (
    // Same shape as the comment cursor (public/cursors/comment.svg) and the
    // bottom-island tool icon, kept visually consistent across affordances.
    <svg width="15" height="15" viewBox="10 -2 65 65" fill="none" stroke="currentColor" strokeWidth="4">
      <path
        d="M15.0034 21.4772C14.8473 13.5164 20.2377 6.51376 27.9735 4.62775L39.2007 1.89052C63.1316 -3.94392 83.5072 19.9531 73.9631 42.6609L71.2066 49.2191C68.5078 55.6402 62.1652 59.7654 55.2014 59.6289L16.6977 58.8739C16.1608 58.8634 15.728 58.4306 15.7175 57.8937L15.0034 21.4772Z"
        strokeLinejoin="round"
      />
    </svg>
  );

  const sidebarHeaderTitle = isCommentsHistoryOpen ? "Comments" : getSidebarHeaderTitle();
  const sidebarHeaderIcon = isCommentsHistoryOpen ? CommentsHeaderIcon : getSidebarHeaderIcon();

  // Function to handle closing panels
  const handleClosePanel = () => {
    // Close Comments history panel
    if (isCommentsHistoryOpen) {
      closeCommentsHistory();
      return;
    }

    // Close Brand Kit assets panel
    if (brandKitAssetsPanel) {
      setBrandKitAssetsPanel(null);
      return;
    }

    // Close Image, Icons, or QR Code panels
    if (showImagePanel) {
      setShowImagePanel(false);
    } else if (showIconsPanel) {
      setShowIconsPanel(false);
    } else if (showQRcodePanel) {
      setShowQRcodePanel(false);
    }

    // Close editing panels by deselecting shapes
    if (selectedShape || selectedShapeIds.length > 0) {
      editor.setSelectedShapes([]);
    }

    // Activate select tool when closing panel
    if (editor.getCurrentToolId() !== "select") {
      editor.setCurrentTool("select");
    }
  };

  const isShowSideBar =
    isCommentsHistoryOpen ||
    brandKitAssetsPanel ||
    showImagePanel ||
    showIconsPanel ||
    showQRcodePanel ||
    (selectedShapes.length === 1 && (selectedShape?.type === "line" || selectedShape?.type === "arrow")) ||
    ((selectedShapes.length > 1 ||
      (selectedShape && selectedShape.props?.w) ||
      (selectedShape && selectedShape.type === "customDraw") ||
      (selectedShape && selectedShape.type === "group") ||
      (selectedShape && selectedShape.type === "customHighlight") ||
      isQRCodeSelected() ||
      isAnyQRCodeSelected()) &&
      selectedShape?.type !== "customLabel" &&
      selectedShape?.type !== "arrow" &&
      selectedShape?.type !== "line");

  // Right sidebar should disappear entirely when there's nothing to show
  const hasRightSidebarContent =
    isShowSideBar || selectedShape?.type === "customLabel" || showColorPickerSetMode || isSelectedPipette;

  return (
    <>
      <style>{`
        @keyframes qrShake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-4px); }
          30% { transform: translateX(4px); }
          45% { transform: translateX(-3px); }
          60% { transform: translateX(3px); }
          75% { transform: translateX(-2px); }
          90% { transform: translateX(2px); }
        }
        .qr-link-shake {
          animation: qrShake 0.5s ease-in-out;
          border-color: #EE4360 !important;
        }
      `}</style>
      {hasRightSidebarContent && (
        <div>
          {/* Wrapper for right sidebar panels + flap - no overflow so flap is visible */}
          <div
            data-tour="right-sidebar"
            className={cn(
              "h-full !fixed top-0 z-[9999999] w-[256px] pt-[80px] transition-[right] duration-200 pointer-events-none",
              isRightSidebarCollapsed ? "right-[-230px]" : "right-0"
            )}
            style={{ overflow: "visible" }}
          >
            {/* Flap toggle - positioned to align with left sidebar flap */}
            <div
              onClick={() => setIsRightSidebarCollapsed(!isRightSidebarCollapsed)}
              style={{
                position: "absolute",
                top: "calc(60px + (100vh - 60px) / 2 - 27px)",
                left: "0px",
                width: "18px",
                height: "70px",
                clipPath: "polygon(0 21.43%, 100% 0, 100% 100%, 0% 78.57%)",
                backgroundColor: "#E2E2FC",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                pointerEvents: "auto",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  right: 0,
                  clipPath: "polygon(0 21.43%, 100% 0, 100% 100%, 0% 78.57%)",
                  backgroundColor: "var(--color-surface-panel)",
                  margin: "1px",
                  marginRight: 0,
                }}
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="5"
                viewBox="0 0 10 5"
                fill="none"
                style={{
                  position: "relative",
                  zIndex: 1,
                  transform: isRightSidebarCollapsed ? "rotate(90deg)" : "rotate(-90deg)",
                }}
              >
                <path
                  d="M1 0.5L5 4.5L9 0.5"
                  stroke="#2E3860"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Sidebar content area */}
            <div
              data-tour="right-sidebar-content"
              className="absolute top-0 right-0 w-[238px] h-full bg-white pointer-events-auto"
              style={{ paddingTop: "inherit", borderLeft: "1px solid #E2E2FC" }}
            >
              {/* Label color picker */}
              {selectedShape?.type === "customLabel" && (
                <div className="!overflow-y-scroll !overflow-x-hidden h-full p-4 pb-[100px]">
                  <CustomColorPicker editor={editor} shapeColor={props.shapeColor} selectedShape={selectedShape} />
                </div>
              )}

              {/* Color picker set mode */}
              {(showColorPickerSetMode || isSelectedPipette) && (
                <div className="!overflow-y-scroll !overflow-x-hidden h-full p-4 pb-[100px]">
                  <CustomColorPicker
                    showColorPickerSetMode
                    editor={props.editor}
                    shapeColor={props.shapeColor}
                    isSelectedPipette={isSelectedPipette}
                  />
                </div>
              )}

              {/* Main sidebar content */}
              {isShowSideBar && (
                <div className="flex flex-col h-full">
                  {/* Sidebar Header */}
                  {sidebarHeaderTitle && (
                    <div className="flex-shrink-0 z-10 bg-[#E2E2FC] border-b border-[#E2E2FC] px-4 py-3">
                      <div className="flex items-center justify-between gap-2">
                        <h2 className="text-[12px] font-bold text-[#2E3860] leading-5 flex items-center gap-2">
                          {sidebarHeaderIcon}
                          {sidebarHeaderTitle}
                        </h2>

                        <button
                          onClick={handleClosePanel}
                          className="cursor-pointer hover:opacity-70 transition-opacity"
                          aria-label="Close panel"
                        >
                          <CloseIcon />
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex-1 overflow-y-auto overflow-x-hidden">
                    {isCommentsHistoryOpen ? (
                      <ThreadsListPanel />
                    ) : brandKitAssetsPanel ? (
                      <BrandKitAssetsPanel />
                    ) : showImagePanel ? (
                      // Show Image component when showImagePanel is true
                      <Image />
                    ) : showIconsPanel ? (
                      // Show Icons component when showIconsPanel is true
                      <Icons />
                    ) : showQRcodePanel ? (
                      // Show QRCode component when showQRcodePanel is true
                      <QrCode />
                    ) : (
                      <>
                        {/* Image Search - Show when customImage is selected (but not QR code) */}
                        {selectedShape?.type === "customImage" && !isQRCodeSelected() && (
                          <div className="border-b border-[#E2E2FC]">
                            <Image searchOnEnter={true} />
                          </div>
                        )}

                        {/* Icon Search - Show when customSvg is selected */}
                        {selectedShape?.type === "customSvg" && (
                          <div className="border-b border-[#E2E2FC]">
                            <Icons searchOnEnter={true} />
                          </div>
                        )}

                        {/* Hide AlignActions and position controls for drawing shapes */}
                        {selectedShape?.type !== "customDraw" && selectedShape?.type !== "customHighlight" && (
                          <>
                            <AlignActions
                              editor={editor}
                              selectedShape={selectedShape}
                              selectedShapeIds={selectedShapeIds}
                              selectedShapes={selectedShapes}
                            />
                            <div className="flex gap-4 mt-2 items-center ml-auto">
                              <div
                                className={cn(!selectedShape && "pointer-events-none opacity-50", "flex w-full px-4")}
                              >
                                <RotateComponent
                                  setSelectedShape={setSelectedShape}
                                  selectedShape={selectedShape}
                                  editor={editor}
                                  selectedShapeIds={selectedShapeIds}
                                  selectedShapes={selectedShapes}
                                />
                                {selectedShape?.type !== "group" && selectedShape?.type !== "customAddressBox" && (
                                  <>
                                    <FlipComponent
                                      setSelectedShape={setSelectedShape}
                                      selectedShape={selectedShape}
                                      editor={editor}
                                      selectedShapeIds={selectedShapeIds}
                                      isVerticalFlip
                                    />
                                    <div className="w-[1px] h-[20px] bg-[#e0e0e0] mx-1" />
                                    <FlipComponent
                                      setSelectedShape={setSelectedShape}
                                      selectedShape={selectedShape}
                                      editor={editor}
                                      selectedShapeIds={selectedShapeIds}
                                    />
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Mask Operations Component - hide for address box */}
                            {selectedShape?.type !== "customAddressBox" && (
                              <MaskComponent
                                selectedShape={selectedShape}
                                selectedShapes={
                                  selectedShapes.filter((shape): shape is TLShape => shape != null) as TLShape[]
                                }
                                selectedShapeIds={selectedShapeIds}
                                editor={editor}
                              />
                            )}
                          </>
                        )}

                        {/* QR Code Editor */}
                        {isQRCodeSelected() && (
                          <>
                            <div
                              style={{
                                paddingLeft: "8px",
                                paddingRight: "8px",
                                width: "100%",
                              }}
                            >
                              <div
                                style={{
                                  width: "100%",
                                  height: "1px",
                                  backgroundColor: "#E2E2FC",
                                  marginTop: "8px",
                                  marginBottom: "8px",
                                }}
                              />
                            </div>
                            <div className="px-4 mt-4">
                              {/* QR Link Section */}
                              <div className="mb-4">
                                <div className="flex items-center justify-between mb-5 px-2">
                                  <h3 className="font-bold text-[14px] text-[#2E3860] leading-5">QR Link</h3>
                                  <Tooltip
                                    title="Delete QR code"
                                    placement="top"
                                    arrow
                                    classes={{ tooltip: tooltipClasses.tooltip, arrow: tooltipClasses.arrow }}
                                  >
                                    <button
                                      onClick={handleDeleteQRCode}
                                      className="p-1 hover:opacity-70 transition-opacity"
                                      type="button"
                                    >
                                      <TrashIcon />
                                    </button>
                                  </Tooltip>
                                </div>
                                {(() => {
                                  const selectedQRCodes = getAllSelectedQRCodes();
                                  if (selectedQRCodes.length > 0) {
                                    const qrShape = selectedQRCodes[0];
                                    const qrCodeLink = (qrShape?.props as any)?.qrCodeLink || "";
                                    {
                                      const currentLink = qrCodeLink || editableQrCodeUrl || "";
                                      // The field shows/copies the user's raw link; the hover tooltip shows it in full.
                                      const displayedLink = currentLink;

                                      const handleCopyLink = async (e: React.MouseEvent) => {
                                        e.stopPropagation(); // Prevent triggering edit mode
                                        try {
                                          await navigator.clipboard.writeText(displayedLink);
                                          setCopiedTooltipOpen(true);
                                          setTimeout(() => {
                                            setCopiedTooltipOpen(false);
                                          }, 2000);
                                        } catch (err) {
                                          // Fallback for older browsers
                                          const textArea = document.createElement("textarea");
                                          textArea.value = displayedLink;
                                          textArea.style.position = "fixed";
                                          textArea.style.left = "-999999px";
                                          document.body.appendChild(textArea);
                                          textArea.focus();
                                          textArea.select();
                                          try {
                                            document.execCommand("copy");
                                            setCopiedTooltipOpen(true);
                                            setTimeout(() => {
                                              setCopiedTooltipOpen(false);
                                            }, 2000);
                                          } catch (err) {
                                            console.error("Failed to copy:", err);
                                          }
                                          document.body.removeChild(textArea);
                                        }
                                      };

                                      return (
                                        <div>
                                          {isEditingQrCodeUrl ? (
                                            <div className="relative flex items-center">
                                              <input
                                                ref={qrCodeUrlInputRef}
                                                type="text"
                                                value={editableQrCodeUrl}
                                                onChange={(e) => {
                                                  setEditableQrCodeUrl(e.target.value);
                                                }}
                                                onFocus={() => {
                                                  setIsFocusedQrCodeUrlInput(true);
                                                }}
                                                onBlur={(e) => {
                                                  setIsFocusedQrCodeUrlInput(false);
                                                  setIsHoveringQrCodeUrlInput(false);
                                                  setIsHoveringQrCodeUrl(false);
                                                  const newUrl = e.target.value.trim();
                                                  if (newUrl && newUrl !== qrCodeLink) {
                                                    updateQrCodeUrl(newUrl);
                                                  } else if (!newUrl) {
                                                    // User cleared the link → keep it empty and remove it everywhere.
                                                    setEditableQrCodeUrl("");
                                                    clearQrCodeLink();
                                                  }
                                                  setIsEditingQrCodeUrl(false);
                                                }}
                                                onMouseEnter={() => {
                                                  setIsHoveringQrCodeUrlInput(true);
                                                }}
                                                onMouseLeave={() => {
                                                  setIsHoveringQrCodeUrlInput(false);
                                                }}
                                                onKeyDown={(e) => {
                                                  if (e.key === "Enter") {
                                                    e.currentTarget.blur();
                                                  } else if (e.key === "Escape") {
                                                    setEditableQrCodeUrl(qrCodeLink);
                                                    setIsEditingQrCodeUrl(false);
                                                  }
                                                }}
                                                onPaste={(e) => {
                                                  // Keep the paste inside this input — stop it from
                                                  // bubbling to tldraw's canvas paste handlers, which
                                                  // would otherwise drop the URL as a stray text shape.
                                                  e.stopPropagation();
                                                }}
                                                className="w-full text-xs text-[#2E3860] bg-[#ffffff] px-2 py-1.5 pr-8 rounded-[4px] focus:outline-none transition-all"
                                                style={{
                                                  border: "1px solid #E2E2FC",
                                                  marginTop: "4px",
                                                }}
                                                placeholder="Enter QR code URL..."
                                                autoFocus
                                              />
                                              <Tooltip
                                                open={copiedTooltipOpen}
                                                onClose={() => setCopiedTooltipOpen(false)}
                                                title="URL copied!"
                                                placement="top"
                                                arrow
                                                classes={{
                                                  tooltip: classes.tooltipCopied,
                                                  arrow: classes.tooltipArrow,
                                                }}
                                              >
                                                <button
                                                  onClick={handleCopyLink}
                                                  className="absolute right-2 p-1 hover:opacity-70 transition-opacity"
                                                  type="button"
                                                >
                                                  <CopyIcon />
                                                </button>
                                              </Tooltip>
                                            </div>
                                          ) : (
                                            <div
                                              ref={qrLinkBoxRef}
                                              className="cursor-pointer text-xs text-[#2E3860] bg-[#ffffff] px-2 py-1.5 rounded-[4px] min-h-[32px] flex items-center justify-between gap-2 overflow-hidden hover:bg-[#f9f9ff] transition-all relative"
                                              style={{
                                                border: "1px solid #E2E2FC",
                                              }}
                                              onMouseEnter={() => {
                                                setIsHoveringQrCodeUrl(true);
                                              }}
                                              onMouseLeave={() => {
                                                setIsHoveringQrCodeUrl(false);
                                              }}
                                              onClick={() => {
                                                // Open the editor seeded with the current raw link so the user can edit or clear it.
                                                setEditableQrCodeUrl(qrCodeLink);
                                                setIsEditingQrCodeUrl(true);
                                                setTimeout(() => {
                                                  qrCodeUrlInputRef.current?.focus();
                                                  qrCodeUrlInputRef.current?.select();
                                                }, 0);
                                              }}
                                            >
                                              <Tooltip
                                                title={displayedLink || ""}
                                                placement="top"
                                                arrow
                                                classes={{
                                                  tooltip: tooltipClasses.tooltip,
                                                  arrow: tooltipClasses.arrow,
                                                }}
                                              >
                                                <span className="truncate block flex-1 pr-2">
                                                  {displayedLink || "No URL set"}
                                                </span>
                                              </Tooltip>
                                              <Tooltip
                                                open={copiedTooltipOpen}
                                                onClose={() => setCopiedTooltipOpen(false)}
                                                title="URL copied!"
                                                placement="top"
                                                arrow
                                                classes={{
                                                  tooltip: classes.tooltipCopied,
                                                  arrow: classes.tooltipArrow,
                                                }}
                                              >
                                                <button
                                                  onClick={handleCopyLink}
                                                  className="flex-shrink-0 p-1 hover:opacity-70 transition-opacity"
                                                  type="button"
                                                >
                                                  <CopyIcon />
                                                </button>
                                              </Tooltip>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    }
                                  }
                                  return null;
                                })()}
                              </div>

                              {/* QR Code Color Section */}
                              <div className="mb-4">
                                <h3 className="font-bold text-[14px] text-[#2E3860] leading-5 mb-2">QR Code Color</h3>
                                <QrColorPicker
                                  pixelColor={localQrPixelColor}
                                  backgroundColor={localQrBackgroundColor}
                                  onPixelColorChange={handleQrPixelColorChangeWithTransparency}
                                  onBackgroundColorChange={() => {}}
                                  showPixelColor={true}
                                  showBackgroundColor={false}
                                />
                              </div>

                              {/* QR Code Background Section */}
                              <div className="mb-4">
                                <h3 className="font-bold text-[14px] text-[#2E3860] leading-5 mb-2">
                                  QR Code Background
                                </h3>
                                <QrColorPicker
                                  pixelColor={localQrPixelColor}
                                  backgroundColor={localQrBackgroundColor}
                                  onPixelColorChange={() => {}}
                                  onBackgroundColorChange={async (color: string) => {
                                    setLocalQrBackgroundColor(color);
                                    updateQrCodeSettings({ qrBackgroundColor: color });

                                    if (!isBackgroundColorVisible) {
                                      setIsBackgroundColorVisible(true);
                                    }

                                    const selectedQRCodes = getAllSelectedQRCodes();
                                    if (selectedQRCodes.length > 0) {
                                      selectedQRCodes.forEach((qrShape) => {
                                        if (!qrShape) return;
                                        const currentPixelColor =
                                          (qrShape.props as any)?.qrPixelColor || localQrPixelColor || "#000000";

                                        editor.updateShape({
                                          id: qrShape.id,
                                          type: qrShape.type,
                                          props: {
                                            ...qrShape.props,
                                            qrBackgroundColor: color,
                                            isBackgroundVisible: true,
                                          },
                                        } as any);
                                        updateQrCodeAssetThrottled(qrShape, currentPixelColor, color);
                                      });
                                    }
                                  }}
                                  showPixelColor={false}
                                  showBackgroundColor={true}
                                />
                              </div>
                            </div>
                          </>
                        )}

                        {(() => {
                          // Show text parameters only for single text elements or when all selected shapes are text
                          if (selectedShape?.type === "customText" && selectedShapes.length === 1) {
                            return true;
                          }
                          if (selectedShapes.length > 1) {
                            const validShapes = selectedShapes.filter(
                              (shape): shape is NonNullable<typeof shape> => shape != null
                            );
                            return validShapes.every((shape) => shape.type === "customText");
                          }
                          return false;
                        })() && (
                          <div className="flex flex-col gap-4">
                            <TextParameters
                              selectedShape={selectedShape}
                              selectedFont={selectedFont}
                              setSelectedFont={setSelectedFont}
                              selectedShapes={selectedShapes}
                              editor={editor}
                              setSelectedShape={setSelectedShape}
                              fonts={props.fonts}
                            />
                          </div>
                        )}

                        {/* Line/Arrow: only show color control (Fill/Stroke/Effects would error) */}
                        {(selectedShape?.type === "line" || selectedShape?.type === "arrow") && (
                          <>
                            <div style={{ paddingLeft: "8px", paddingRight: "8px", width: "100%" }}>
                              <div
                                style={{
                                  width: "100%",
                                  height: "1px",
                                  backgroundColor: "#E2E2FC",
                                  marginTop: "8px",
                                  marginBottom: "8px",
                                }}
                              />
                            </div>
                            <LineArrowColorSidebar
                              editor={editor}
                              selectedShape={selectedShape}
                              setSelectedShape={setSelectedShape}
                            />
                          </>
                        )}
                        {/* Show SidebarBottomTool for other shapes (except groups, lines, arrows, address boxes).
                            QR codes are allowed — Effects section enables drop shadow on QR. */}
                        {selectedShape?.type !== "group" &&
                          selectedShape?.type !== "line" &&
                          selectedShape?.type !== "arrow" &&
                          selectedShape?.type !== "customAddressBox" && (
                            <SidebarBottomTool
                              editor={editor}
                              max={1}
                              min={0}
                              step={0.1}
                              title={"Opacity"}
                              sliderValue={selectedShape?.props?.transparency || 0}
                              setSliderValue={(value) => handleSliderChange("transparency", value)}
                              handleSliderChange={handleSliderChange}
                              selectedShape={selectedShape}
                              prop="transparency"
                              setSelectedShape={setSelectedShape}
                              selectedShapes={selectedShapes}
                            />
                          )}

                        {/* Add weight slider for drawing shapes */}
                        {(selectedShape?.type === "customDraw" || selectedShape?.type === "customHighlight") && (
                          <div className="px-4 flex flex-col gap-2 mt-4">
                            <SideBarSlider
                              editor={editor}
                              max={10}
                              min={0.25}
                              step={0.25}
                              title={selectedShape?.type === "customHighlight" ? "Highlighter Weight" : "Pen Weight"}
                              sliderValue={selectedShape?.props?.weight || 3.5}
                              setSliderValue={(value) => handleSliderChange("weight", value)}
                              prop="weight"
                              selectedShape={selectedShape}
                              setSelectedShape={setSelectedShape}
                              selectedShapes={selectedShapes}
                              tooltip="Adjust the thickness of your drawing strokes"
                              placement="bottom"
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CustomStylePanel;
